// Short "your order is ready" SMS body.
//
// The full ready message (greeting, item list, store address, sign-off)
// runs 500+ characters, and its bullets, "×" and the en dash in store names
// like "Ottawa – Glebe" are outside the GSM-7 alphabet. One such character
// switches the whole SMS to UCS-2, where a segment holds 70 characters
// instead of 160, so the old body went out as 8+ segments per send.
//
// So the SMS gets its own body: folded to plain ASCII (a subset of GSM-7
// that needs no escape characters), and never longer than one 160-char
// segment. The email keeps the long message.

export const SMS_MAX_LENGTH = 160

const TYPOGRAPHIC: Array<[RegExp, string]> = [
  [/[\u2010-\u2015\u2212]/g, '-'],              // hyphens, en/em dash, minus
  [/[\u2018\u2019\u201a\u201b\u2032\u02bb\u02bc]/g, "'"], // curly quotes, prime, okina
  [/[\u201c\u201d\u201e\u201f\u2033]/g, '"'],   // curly double quotes
  [/\u2026/g, '...'],
  [/\u00d7/g, 'x'],
  [/[\u2022\u00b7]/g, '-'],                     // bullet, middle dot
  [/[\u00a0\u2000-\u200b\u202f\u205f]/g, ' '],  // non-breaking / odd spaces
]

// Letters NFKD leaves whole (no combining mark to strip), which UNSAFE
// would otherwise delete: "S\u00f8ren" must not become "Sren".
const TRANSLITERATE: Record<string, string> = {
  '\u00df': 'ss', '\u1e9e': 'SS', '\u00e6': 'ae', '\u00c6': 'AE',
  '\u0153': 'oe', '\u0152': 'OE', '\u00f8': 'o', '\u00d8': 'O',
  '\u0142': 'l', '\u0141': 'L', '\u0111': 'd', '\u0110': 'D',
  '\u00f0': 'd', '\u00d0': 'D', '\u00fe': 'th', '\u00de': 'Th',
  '\u0131': 'i',
}
const TRANSLITERATE_RE = new RegExp(`[${Object.keys(TRANSLITERATE).join('')}]`, 'g')

// Everything left must be in this set. All of it is GSM-7 basic (one
// septet each), so length <= 160 really is one segment. Deliberately
// excludes [ ] { } \ ~ ^ | -- GSM-7 sends those as two septets.
const UNSAFE = /[^A-Za-z0-9 .,:;!?'"()#&@%+\-/*=<>$_]/g

function fold(value: string | null | undefined): string {
  let s = (value ?? '').normalize('NFKD').replace(/\p{M}/gu, '')
  s = s.replace(TRANSLITERATE_RE, (c) => TRANSLITERATE[c])
  for (const [re, rep] of TYPOGRAPHIC) s = s.replace(re, rep)
  return s.replace(/\s+/g, ' ')
}

/** Fold free text (a name, a store name) to one line of plain ASCII. */
export function toSmsText(value: string | null | undefined): string {
  return fold(value).replace(UNSAFE, '').replace(/ {2,}/g, ' ').trim()
}

/** True when folding keeps every letter -- false for a name in a script
 *  with no ASCII form (CJK, Cyrillic, ...), which would come out mangled. */
function foldsCleanly(value: string | null | undefined): boolean {
  const letters = fold(value).match(/\p{L}/gu) ?? []
  return letters.every((c) => /[A-Za-z]/.test(c))
}

export interface ReadySmsArgs {
  customerName: string | null | undefined
  /** Receipt number, with or without the leading '#'. */
  orderNumber: string
  storeName: string | null | undefined
  /** Street line only -- city/province/postal code do not fit. */
  storeStreet: string | null | undefined
  isDelivery?: boolean
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Build the ready SMS, dropping optional parts (street, then name, then
 *  the long claim wording) until it fits one segment. Always
 *  <= SMS_MAX_LENGTH. */
export function buildReadySms(args: ReadySmsArgs): string {
  // A name that cannot be spelled in ASCII is left out, not mangled.
  const name = foldsCleanly(args.customerName) ? toSmsText(args.customerName) : ''
  const order = '#' + toSmsText(args.orderNumber).replace(/^#/, '')
  const store = toSmsText(args.storeName) || 'our store'
  const street = toSmsText(args.storeStreet)

  const hi = (rest: string) => (name ? `Hi ${name}, ${rest}` : capitalize(rest))

  // Richest wording first; the first one that fits wins.
  let candidates: string[]
  let leanest: (st: string) => string
  if (args.isDelivery) {
    leanest = (st) => capitalize(`your order ${order} from ${st} is ready and on its way.`)
    candidates = [hi(`your order ${order} from ${store} is ready and on its way.`)]
  } else {
    const longClaim = 'Please pick it up within 48 hours or it will be cancelled.'
    const shortClaim = 'Pick up within 48 hrs or it will be cancelled.'
    const ready = `your order ${order} at ${store} is ready for pickup`
    leanest = (st) => capitalize(`your order ${order} at ${st} is ready for pickup.`)
    // The street goes first, then the name, then the long claim wording.
    // A name too long to fit even without the street is dropped before
    // the street is.
    candidates = [
      ...(street ? [hi(`${ready} at ${street}. ${longClaim}`)] : []),
      hi(`${ready}. ${longClaim}`),
      ...(street ? [capitalize(`${ready} at ${street}. ${longClaim}`)] : []),
      capitalize(`${ready}. ${longClaim}`),
      capitalize(`${ready}. ${shortClaim}`),
    ]
  }
  candidates.push(leanest(store))

  for (const c of candidates) {
    if (c.length <= SMS_MAX_LENGTH) return c
  }
  // Only an absurdly long store name (or order number) gets here: cut the
  // store name to whatever room the leanest wording leaves.
  const room = SMS_MAX_LENGTH - leanest('').length
  return leanest(store.slice(0, Math.max(room, 0)).trim()).slice(0, SMS_MAX_LENGTH)
}
